import React, { FC, memo, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  DialogActions,
  Button,
  makeStyles,
  colors,
  Typography,
  Box,
  Tooltip,
  Card,
  CircularProgress,
  TableContainer
} from '@material-ui/core';

import { copyToClipboard, formatCurrency } from 'src/utils';
import { CommonAxiosResponse } from 'src/types';
import Api from 'src/services/ApiService';
import { format } from 'date-fns';
import { AlternatingColorTableRow, LinkComponent } from 'src/components';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import useResolution from 'src/hooks/useResolution';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { AlternateRowContainer } from 'src/views/product/component/AlternateRowContainer';
import { TextRow } from 'src/views/product/component/TextRow';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles(() => ({
  root: {},
  imgPreview: {
    width: undefined,
    height: 50
  },
  priceInput: {
    height: 30
  },
  lightGrey: {
    backgroundColor: colors.blueGrey[50]
  },
  table: {
    border: '1px solid #a4abba'
  },
  tableCell: {
    textAlign: 'center',
    borderBottom: '1px solid #a4abba'
  },
  tableRow: {
    borderBottom: '1px solid #a4abba'
  }
}));

export type LiabilityChequeData = {
  id?: number;
  cheque_no?: string;
  pdc_date?: string;
  amount?: number;
  supplier?: string;
};

type LiabilityCheqyeResponse = {
  success?: boolean;
  data?: LiabilityChequeData[];
};

interface Props {
  open: boolean;
  liabilityPayload?: {
    pdc_date_week?: string;
    supplier_id?: number;
  };
  onClose: () => void;
}

const Component: FC<Props> = ({ open, liabilityPayload, onClose }) => {
  const classes = useStyles();
  const { isMobile } = useResolution();

  const [chequeLiabilityData, setChequeLiabilityData] = useState<
    LiabilityChequeData[]
  >();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getLiabilityCheque = (payload: {
    supplier_id?: number;
    pdc_date_from?: string;
    pdc_date_to?: string;
  }): any => {
    return Api.get(`po/liability/cheque/get`, payload);
  };

  const fetchLiabilityCheque = async (params?: {
    pdc_date_week?: string;
    supplier_id?: number;
  }) => {
    // api.ts this later
    try {
      setIsLoading(true);
      const [rawStartDate, rawEndDate] =
        params?.pdc_date_week?.split(' - ') || [];
      const startDate = format(new Date(rawStartDate), 'yyyy-MM-dd');
      const endDate = format(new Date(rawEndDate), 'yyyy-MM-dd');

      const response: CommonAxiosResponse<LiabilityCheqyeResponse> = await getLiabilityCheque(
        {
          pdc_date_from: startDate,
          pdc_date_to: endDate,
          supplier_id: params?.supplier_id
        }
      );

      if (response?.originalData?.data) {
        // Handle successful response
        setChequeLiabilityData(response?.originalData?.data);
        setIsLoading(false);
      } else {
        console.error('Error fetching liability cheque:', response);
      }
    } catch (error) {
      // Handle network or other errors
      console.error('Network error while fetching liability data:', error);
      setIsLoading(false);
    }
  };

  const onHandleCopyChequeNo = (chequeNo?: string) => {
    copyToClipboard(chequeNo || '');
  };

  useEffect(() => {
    if (open) {
      fetchLiabilityCheque(liabilityPayload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, liabilityPayload]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h4">Cheque List Liability</Typography>
      </DialogTitle>
      <DialogContent style={{ minHeight: 200 }}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 300
            }}
          >
            <CircularProgress size={100} />
          </Box>
        ) : (
          <>
            {isMobile ? (
              <>
                {chequeLiabilityData && chequeLiabilityData.length > 0 ? (
                  <Card>
                    <PerfectScrollbar>
                      {chequeLiabilityData?.map((row, index) => (
                        <AlternateRowContainer index={index} key={row?.id}>
                          <div style={{ padding: 10 }}>
                            <TextRow
                              label="Cheque no"
                              value={row?.cheque_no || '--'}
                              component={
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  <FileCopyIcon
                                    style={{
                                      cursor: 'pointer',
                                      marginRight: 5
                                    }}
                                    onClick={() =>
                                      onHandleCopyChequeNo(row?.cheque_no || '')
                                    }
                                  />
                                  <LinkComponent
                                    openInNewTab
                                    href={`/app/cheque/${row?.id}`}
                                    title={row?.cheque_no ?? ''}
                                    font="Roboto, Helvetica, Arial, sans-serif "
                                  />
                                </Box>
                              }
                            />

                            <TextRow
                              label="PDC Date"
                              value={row?.pdc_date}
                              noBorderBottom
                            />
                            <TextRow
                              label="Amount"
                              value={formatCurrency(row?.amount || 0)}
                              noBorderBottom
                            />
                            <TextRow
                              label="Supplier"
                              value={row?.supplier}
                              noBorderBottom
                            />
                          </div>
                        </AlternateRowContainer>
                      ))}
                    </PerfectScrollbar>
                  </Card>
                ) : (
                  <Alert severity="warning">
                    <Typography variant="h5">
                      There are no cheque records available for the specified
                      date range.
                    </Typography>
                  </Alert>
                )}
              </>
            ) : (
              <>
                {chequeLiabilityData && chequeLiabilityData.length > 0 ? (
                  <TableContainer
                    style={{ maxHeight: 800, border: '2px solid gray' }}
                  >
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.tableCell}>
                            Cheque No
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            PDC Date
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            Amount
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            Supplier
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {chequeLiabilityData?.map((row) => (
                          <AlternatingColorTableRow key={row?.id}>
                            <TableCell className={classes.tableCell}>
                              <Box
                                sx={{ display: 'flex', alignItems: 'center' }}
                              >
                                <Tooltip title="copy">
                                  <FileCopyIcon
                                    style={{
                                      cursor: 'pointer',
                                      marginRight: 10
                                    }}
                                    onClick={() =>
                                      onHandleCopyChequeNo(row?.cheque_no)
                                    }
                                  />
                                </Tooltip>
                                <LinkComponent
                                  href={`/app/cheque/${row?.id}`}
                                  title={`${row?.cheque_no}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                />
                              </Box>
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              {row?.pdc_date || '--'}
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              {formatCurrency(row?.amount || 0)}
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              {row?.supplier || '--'}
                            </TableCell>
                          </AlternatingColorTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="warning">
                    <Typography variant="h5">
                      There are no cheque records available for the specified
                      date range.
                    </Typography>
                  </Alert>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const LiabilityChequeDialog = memo(Component);
